/**
 * Shared payroll monthly attendance loading (same rules as PayrollPage).
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { resolveCollectionName } from "../services/attendanceCollections";
import {
  attributedMsInDay,
  collectPunchTimes,
  computeSessionsFromTimestamps,
  extractEmpKeyFromData,
  mergeAndDedupeTimestamps,
} from "./staffDailyHours";

/** Manual half-day adjustments from Payroll (adds ½ day unit when merged into monthly rows). */
export const COLLECTION_PAYROLL_HALF_DAY_MARKS = "payrollHalfDayMarks";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function yearsBetween(startDate, endDate) {
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) return null;
  if (!(endDate instanceof Date) || isNaN(endDate.getTime())) return null;
  const ms = endDate.getTime() - startDate.getTime();
  if (!Number.isFinite(ms) || ms < 0) return 0;
  return round2(ms / (365.25 * 24 * 60 * 60 * 1000));
}

function tenurePartsBetween(startDate, endDate) {
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) return null;
  if (!(endDate instanceof Date) || isNaN(endDate.getTime())) return null;
  if (endDate < startDate) return { years: 0, months: 0 };

  let years = endDate.getFullYear() - startDate.getFullYear();
  let months = endDate.getMonth() - startDate.getMonth();
  const endDay = endDate.getDate();
  const startDay = startDate.getDate();

  if (endDay < startDay) months -= 1;
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return { years: 0, months: 0 };
  return { years, months };
}

/** Display present-day total (may include ½ from half days). */
export function formatPresentDayUnits(n) {
  const x = Number(n) || 0;
  if (Math.abs(x - Math.round(x)) < 0.005) return String(Math.round(x));
  return (Math.round(x * 10) / 10).toFixed(1);
}

export function toDateSafe(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (typeof value === "object" && Number.isFinite(value.seconds)) {
    const ms = Number(value.seconds) * 1000 + Math.floor(Number(value.nanoseconds || 0) / 1000000);
    const tsDate = new Date(ms);
    return isNaN(tsDate.getTime()) ? null : tsDate;
  }
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string") {
    const direct = new Date(value);
    if (!isNaN(direct)) return direct;
    const m = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const d = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const y = Number(m[3]);
      const parsed = new Date(y, mo, d);
      return isNaN(parsed) ? null : parsed;
    }
  }
  return null;
}

export function firstDefined(obj, keys) {
  for (const k of keys) {
    if (obj?.[k] !== undefined && obj?.[k] !== null && obj?.[k] !== "") return obj[k];
  }
  return null;
}

export function norm(v) {
  return String(v || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function isWorkingEmployee(data = {}) {
  const status = String(
    firstDefined(data, [
      "status",
      "Status",
      "employmentStatus",
      "employeeStatus",
      "empStatus",
      "emp_status",
      "workStatus",
    ]) || ""
  )
    .trim()
    .toLowerCase();
  const compactStatus = status.replace(/[\s_-]/g, "");
  const isActiveFlag = firstDefined(data, ["isActive", "active", "Active", "is_active"]);
  const hasLeft = firstDefined(data, ["left", "isLeft", "terminated", "Left", "is_left"]);
  const leavingDate = firstDefined(data, [
    "leavingDate",
    "relievingDate",
    "exitDate",
    "leftDate",
    "LeftDate",
    "dateOfLeaving",
  ]);

  if (typeof isActiveFlag === "boolean" && !isActiveFlag) return false;
  if (typeof hasLeft === "boolean" && hasLeft) return false;
  if (leavingDate) return false;

  const blocked = [
    "left",
    "inactive",
    "terminated",
    "resigned",
    "exemployee",
    "removed",
    "relieved",
    "quit",
  ];
  if (blocked.includes(compactStatus)) return false;

  if (["working", "active", "online", "present", "onroll"].includes(compactStatus)) return true;

  return true;
}

export function ymd(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate()
  ).padStart(2, "0")}`;
}

/** Objects that often wrap Flutter / mobile punch payloads. */
function attendanceFieldRoots(attData = {}) {
  const d = attData || {};
  const roots = [d];
  const push = (x) => {
    if (x && typeof x === "object" && !Array.isArray(x)) roots.push(x);
  };
  push(d.raw);
  push(d.payload);
  push(d.meta);
  push(d.data);
  push(d.fields);
  push(d.attendance);
  push(d.record);
  push(d.details);
  return roots;
}

function firstDefinedInRoots(roots, keys) {
  for (const obj of roots) {
    const v = firstDefined(obj, keys);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
}

function compactAlnum(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_\-–—]+/g, "");
}

function explicitHalfFieldFalse(val) {
  if (val === false || val === 0) return true;
  const t = String(val).trim().toLowerCase();
  return t === "false" || t === "no" || t === "0" || t === "full" || t === "fullday" || t === "wholeday";
}

/** Merge punch instants from root + nested objects (Flutter). */
export function collectPunchTimesAllRoots(attData = {}) {
  const roots = attendanceFieldRoots(attData);
  const flat = [];
  for (const r of roots) {
    flat.push(...collectPunchTimes(r));
  }
  return mergeAndDedupeTimestamps(flat, 10);
}

function dayBoundsFromYmdKey(dayKey) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey || "").trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const da = Number(m[3]);
  const startOfDay = new Date(y, mo, da, 0, 0, 0, 0);
  const endOfDay = new Date(y, mo, da + 1, 0, 0, 0, 0);
  return { startOfDay, endOfDay };
}

/**
 * Worked hours from deduped punch instants on a calendar day (local `yyyy-mm-dd`).
 * Uses paired in/out sessions; if attributed time is 0 but there are 2+ punches, uses first→last span.
 */
export function workedHoursFromMergedTimesForDay(mergedTimes, dayKey) {
  const bounds = dayBoundsFromYmdKey(dayKey);
  if (!bounds || !mergedTimes?.length) return 0;
  const { sessions } = computeSessionsFromTimestamps(mergedTimes);
  let ms = attributedMsInDay(sessions, bounds.startOfDay, bounds.endOfDay);
  if (ms <= 0 && mergedTimes.length >= 2) {
    const span = mergedTimes[mergedTimes.length - 1].getTime() - mergedTimes[0].getTime();
    if (span > 0) ms = span;
  }
  return ms / 3600000;
}

/** After dedupe, this many+ punch instants in a day ⇒ treat as full day (avoids odd in/out pairing understating hours). */
export const MULTI_PUNCH_FULL_DAY_MIN_COUNT = 3;

/**
 * Payroll rule: half day if worked &lt; 5 hours, or if worked ≥ 2 hours and &lt; 6 hours
 * (union ⇒ 0 &lt; hours &lt; 6 counts as half; ≥ 6 full).
 */
export function dayUnitFromWorkedHours(hours) {
  if (hours == null || !Number.isFinite(hours) || hours <= 0) return 0;
  const unionHalf = (hours > 0 && hours < 5) || (hours >= 2 && hours < 6);
  if (unionHalf) return 0.5;
  if (hours >= 6) return 1;
  return 0;
}

/**
 * Field / flag–only inference. Returns `null` when there is no clear signal (defer to hours rule).
 */
export function inferAttendanceDocWeightFromFields(attData = {}) {
  const roots = attendanceFieldRoots(attData);

  const explicitKeys = [
    "isHalfDay",
    "halfDay",
    "HalfDay",
    "is_half_day",
    "half_day",
    "halfday",
    "isHalfday",
    "IsHalfDay",
    "hDay",
    "HDay",
    "half_day_leave",
    "halfDayLeave",
    "isHalfDayLeave",
  ];
  for (const key of explicitKeys) {
    const v = firstDefinedInRoots(roots, [key]);
    if (v === undefined || v === null || v === "") continue;
    if (explicitHalfFieldFalse(v)) continue;
    if (v === true) return 0.5;
    if (typeof v === "number" && v > 0 && v < 1) return Math.min(1, v);
    if (v === 1 || v === "1") return 0.5;
    const t = String(v).trim().toLowerCase();
    if (t === "true" || t === "yes" || t === "y" || t === "half" || t === "hd") return 0.5;
  }

  const typeVal = firstDefinedInRoots(roots, [
    "dayType",
    "DayType",
    "attendanceType",
    "AttendanceType",
    "attendanceStatus",
    "AttendanceStatus",
    "punchType",
    "PunchType",
    "sessionType",
    "SessionType",
    "status",
    "Status",
    "leaveType",
    "LeaveType",
    "leave_type",
    "recordType",
    "RecordType",
    "entryType",
    "EntryType",
    "markedAs",
    "MarkedAs",
    "punchedAs",
    "PunchedAs",
    "presentStatus",
    "PresentStatus",
    "dayStatus",
    "DayStatus",
    "shiftType",
    "ShiftType",
    "workType",
    "WorkType",
  ]);
  if (typeVal != null && typeVal !== "") {
    const raw = String(typeVal).trim().toLowerCase();
    if (raw.includes("½") || raw.includes("half")) {
      if (!/\bnon[\s_-]*half\b/i.test(raw) && !/full\s*day/i.test(raw)) {
        return 0.5;
      }
    }
    const typeStr = compactAlnum(typeVal);
    if (
      [
        "half",
        "halfday",
        "halfdayleave",
        "halfleave",
        "hd",
        "firsthalf",
        "secondhalf",
        "morningonly",
        "afternoononly",
        "halfshift",
        "shortday",
        "partialday",
        "presenthalf",
        "halfpresent",
      ].includes(typeStr) ||
      typeStr === "0.5"
    ) {
      return 0.5;
    }
  }

  const fracRaw = firstDefinedInRoots(roots, [
    "dayFraction",
    "presentFraction",
    "attendanceFraction",
    "workFraction",
    "paidDays",
    "PaidDays",
    "presentDays",
    "leaveDuration",
    "LeaveDuration",
    "durationDays",
    "dayCount",
    "workingDayFraction",
  ]);
  const frac = Number(fracRaw);
  if (Number.isFinite(frac) && frac >= 1) {
    return 1;
  }
  if (Number.isFinite(frac) && frac > 0 && frac < 1) {
    return Math.min(1, Math.max(0, frac));
  }

  const fullFlags = firstDefinedInRoots(roots, [
    "isFullDay",
    "fullDay",
    "FullDay",
    "is_full_day",
    "full_day",
    "fullDayPresent",
    "isPresentFullDay",
  ]);
  if (fullFlags === true || fullFlags === 1 || String(fullFlags).toLowerCase() === "true") {
    return 1;
  }

  const fullType = firstDefinedInRoots(roots, [
    "dayType",
    "DayType",
    "attendanceType",
    "AttendanceType",
    "attendanceStatus",
    "AttendanceStatus",
    "status",
    "Status",
    "recordType",
    "RecordType",
  ]);
  if (fullType != null && fullType !== "") {
    const c = compactAlnum(fullType);
    if (
      [
        "present",
        "fullday",
        "fulldaypresent",
        "complete",
        "wholeday",
        "full",
        "fullpresent",
        "presentfullday",
        "fulldaywork",
        "fulldayattendance",
      ].includes(c)
    ) {
      return 1;
    }
  }

  return null;
}

/**
 * Single-doc weight for UI: fields first, else hours on that doc’s calendar day, else full day.
 */
export function inferAttendanceDocWeight(attData = {}) {
  const f = inferAttendanceDocWeightFromFields(attData);
  if (f != null) return f;
  const times = collectPunchTimesAllRoots(attData);
  if (!times.length) return 1;
  if (times.length >= MULTI_PUNCH_FULL_DAY_MIN_COUNT) return 1;
  const dayGuess = ymd(times[0]);
  const hours = workedHoursFromMergedTimesForDay(times, dayGuess);
  if (hours > 0) return dayUnitFromWorkedHours(hours);
  return 1;
}

export function extractDayKey(attData = {}) {
  const explicit = firstDefined(attData, [
    "date",
    "Date",
    "timestamp",
    "Timestamp",
    "attendanceDate",
    "AttendanceDate",
    "punchDate",
    "punchDateTime",
    "dateString",
    "todayDate",
    "dayDate",
  ]);
  if (explicit?.toDate && typeof explicit.toDate === "function") {
    return ymd(explicit.toDate());
  }
  if (explicit instanceof Date && !isNaN(explicit)) {
    return ymd(explicit);
  }
  if (explicit) {
    const s = String(explicit).trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (dmy) {
      return `${dmy[3]}-${String(Number(dmy[2])).padStart(2, "0")}-${String(
        Number(dmy[1])
      ).padStart(2, "0")}`;
    }
  }
  return null;
}

export function monthTitle(dateObj) {
  return dateObj.toLocaleString("en-IN", { month: "long", year: "numeric" });
}

export function isSunday(dt) {
  return dt.getDay() === 0;
}

export function cutoffDateForSelectedMonth(selectedMonthDate) {
  const now = new Date();
  const sameMonth =
    now.getFullYear() === selectedMonthDate.getFullYear() &&
    now.getMonth() === selectedMonthDate.getMonth();
  return sameMonth
    ? new Date(now.getFullYear(), now.getMonth(), now.getDate())
    : new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth() + 1, 0);
}

/** Calendar yyyy-mm-dd from a holidays collection doc (any record type). */
export function holidayDocDateToYmd(data) {
  if (!data || typeof data !== "object") return null;
  const ts = data.dateTs;
  if (ts != null && typeof ts.toDate === "function") {
    return ymd(ts.toDate());
  }
  const s = (data.dateStr ?? data.date)?.toString()?.trim();
  if (s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
    const t = new Date(s);
    if (!isNaN(t.getTime())) return ymd(t);
  }
  return null;
}

/** Holidays doc is per-employee paid leave (cash leave), including legacy rows. */
export function isEmployeeCashLeaveDoc(data) {
  if (!data || typeof data !== "object") return false;
  const recordType = String(data.recordType || "").trim().toLowerCase();
  if (recordType === "employee_cash_leave") return true;
  if (data.countsAsEmployeeLeave === true) return true;
  if (recordType === "company_calendar") return false;
  const ids = Array.isArray(data.employeeIds) ? data.employeeIds : [];
  return ids.some((id) => String(id || "").trim());
}

function normalizeDocToHolidayYmd(data) {
  if (!data || typeof data !== "object") return null;
  if (isEmployeeCashLeaveDoc(data)) {
    return null;
  }
  return holidayDocDateToYmd(data);
}

/**
 * Paid staff-only leave: yyyy-mm-dd per employee id (keys: trimmed lowercase + norm(id) for lookup).
 * Not merged into the global company holiday set — use with per-employee expected days / absence.
 */
export function buildEmployeeCashLeaveYmdByEmployee(docSnaps, windowStart, windowEndInclusive) {
  /** @type {Map<string, Set<string>>} */
  const map = new Map();
  const ws = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
  const we = new Date(
    windowEndInclusive.getFullYear(),
    windowEndInclusive.getMonth(),
    windowEndInclusive.getDate()
  );
  for (const docSnap of docSnaps) {
    const data = docSnap.data() || {};
    if (!isEmployeeCashLeaveDoc(data)) continue;
    const ymdKey = holidayDocDateToYmd(data);
    if (!ymdKey) continue;
    const dt = parseFlexibleDateString(ymdKey);
    if (!dt) continue;
    const day = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    if (day < ws || day > we) continue;
    const ids = Array.isArray(data.employeeIds) ? data.employeeIds : [];
    for (const raw of ids) {
      const id = String(raw || "").trim();
      if (!id) continue;
      const keys = new Set([id.toLowerCase(), norm(id)].filter(Boolean));
      for (const k of keys) {
        if (!map.has(k)) map.set(k, new Set());
        map.get(k).add(ymdKey);
      }
    }
  }
  return map;
}

/** Union of cash-leave ymds for an employee row id (matches HolidaysPage + roster ids). */
export function employeeCashLeaveYmdSet(cashLeaveByEmp, employeeId) {
  const out = new Set();
  const id = String(employeeId || "").trim();
  if (!id) return out;
  const a = id.toLowerCase();
  const b = norm(id);
  for (const k of [a, b]) {
    const s = cashLeaveByEmp.get(k);
    if (s) for (const y of s) out.add(y);
  }
  return out;
}

/** yyyy-mm-dd → label from `employee_cash_leave` holidays docs (per employee id keys). */
export function buildEmployeeCashLeaveLabelsByEmployee(docSnaps, windowStart, windowEndInclusive) {
  /** @type {Map<string, Map<string, string>>} */
  const map = new Map();
  const ws = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
  const we = new Date(
    windowEndInclusive.getFullYear(),
    windowEndInclusive.getMonth(),
    windowEndInclusive.getDate()
  );
  for (const docSnap of docSnaps) {
    const data = docSnap.data() || {};
    if (!isEmployeeCashLeaveDoc(data)) continue;
    const ymdKey = holidayDocDateToYmd(data);
    if (!ymdKey) continue;
    const dt = parseFlexibleDateString(ymdKey);
    if (!dt) continue;
    const day = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    if (day < ws || day > we) continue;
    const label = String(data.name || data.description || "Leave").trim() || "Leave";
    const ids = Array.isArray(data.employeeIds) ? data.employeeIds : [];
    for (const raw of ids) {
      const id = String(raw || "").trim();
      if (!id) continue;
      const keys = new Set([id.toLowerCase(), norm(id)].filter(Boolean));
      for (const k of keys) {
        if (!map.has(k)) map.set(k, new Map());
        map.get(k).set(ymdKey, label);
      }
    }
  }
  return map;
}

/** Label for one employee leave day; falls back to "Leave". */
export function employeeCashLeaveLabelForDay(labelsByEmp, employeeId, ymdKey) {
  const id = String(employeeId || "").trim();
  if (!id || !ymdKey) return "Leave";
  for (const k of [id.toLowerCase(), norm(id)]) {
    const m = labelsByEmp.get(k);
    if (m?.has(ymdKey)) return m.get(ymdKey);
  }
  return "Leave";
}

export function buildHolidayYmdSetFromDocs(docSnaps, windowStart, windowEndInclusive) {
  const set = new Set();
  const ws = new Date(windowStart.getFullYear(), windowStart.getMonth(), windowStart.getDate());
  const we = new Date(
    windowEndInclusive.getFullYear(),
    windowEndInclusive.getMonth(),
    windowEndInclusive.getDate()
  );
  for (const docSnap of docSnaps) {
    const key = normalizeDocToHolidayYmd(docSnap.data() || {});
    if (!key) continue;
    const dt = parseFlexibleDateString(key);
    if (!dt) continue;
    const day = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    if (day >= ws && day <= we) set.add(key);
  }
  return set;
}

export function getWorkingDaysTillCutoff(selectedMonthDate, holidayYmdSet = new Set()) {
  const cutoff = cutoffDateForSelectedMonth(selectedMonthDate);

  const days = [];
  const start = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth(), 1);
  for (let d = new Date(start); d <= cutoff; d.setDate(d.getDate() + 1)) {
    if (isSunday(d)) continue;
    const key = ymd(d);
    if (holidayYmdSet.has(key)) continue;
    days.push(key);
  }
  return days;
}

/** All Mon–Sat style working days in the full calendar month (no cutoff). */
export function getWorkingDayYmdsFullMonth(selectedMonthDate, holidayYmdSet = new Set()) {
  const end = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth() + 1, 0);
  const days = [];
  const start = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth(), 1);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    if (isSunday(d)) continue;
    const key = ymd(d);
    if (holidayYmdSet.has(key)) continue;
    days.push(key);
  }
  return days;
}

export function parseFlexibleDateString(s) {
  if (!s || typeof s !== "string") return null;
  const t = s.trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  const d = new Date(t);
  if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return null;
}

/** Absent / no-punch dates on or before cutoff (not the same as paid leave). */
export function absentDatesUpToCutoff(absentDateStrings, cutoffDate) {
  if (!absentDateStrings?.length) return [];
  const c = new Date(cutoffDate.getFullYear(), cutoffDate.getMonth(), cutoffDate.getDate());
  return absentDateStrings.filter((s) => {
    const dt = parseFlexibleDateString(s);
    if (!dt) return false;
    const d = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    return d <= c;
  });
}

/** @deprecated Use {@link absentDatesUpToCutoff} — these are absent days, not leave balances. */
export const leaveDatesUpToCutoff = absentDatesUpToCutoff;

export async function fetchAttendanceForMonth(collectionName, monthStart, monthEnd) {
  const startStr = ymd(monthStart);
  const endStr = ymd(monthEnd);
  const tryQuery = async (q) => {
    try {
      return await getDocs(q);
    } catch {
      return null;
    }
  };

  const qStr = query(
    collection(db, collectionName),
    where("timestamp", ">=", startStr),
    where("timestamp", "<", endStr)
  );
  const qTs = query(
    collection(db, collectionName),
    where("timestamp", ">=", Timestamp.fromDate(monthStart)),
    where("timestamp", "<", Timestamp.fromDate(monthEnd))
  );

  const snapStr = await tryQuery(qStr);
  if (snapStr && snapStr.size > 0) {
    return { snap: snapStr, mode: "month (timestamp string)" };
  }
  const snapTs = await tryQuery(qTs);
  if (snapTs && snapTs.size > 0) {
    return { snap: snapTs, mode: "month (timestamp)" };
  }
  if (snapStr && snapTs && snapStr.size === 0 && snapTs.size === 0) {
    const probe = await tryQuery(query(collection(db, collectionName), limit(1)));
    if (probe && probe.size > 0) {
      const full = await getDocs(collection(db, collectionName));
      return { snap: full, mode: "full (month query empty; may use punchTime/other field)" };
    }
    return { snap: snapStr, mode: "month (empty)" };
  }
  const full = await getDocs(collection(db, collectionName));
  return { snap: full, mode: "full collection" };
}

/** Local calendar yyyy-mm-dd for a Date (strip time). */
export function calendarDayKeyFromDate(dateObj) {
  if (!dateObj || Number.isNaN(dateObj.getTime())) return null;
  return ymd(new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
}

/**
 * Resolve calendar day and raw instant from one attendance / punch document (same rules as monthly rollup).
 * @returns {{ dayKey: string, dateObj: Date } | null}
 */
export function getAttendanceDocDayKeyAndDate(d, docSnapId) {
  const att = d || {};
  const explicitDayKey = extractDayKey(att);
  let dayKey = explicitDayKey;
  let dateObj = null;
  if (explicitDayKey) {
    dateObj = toDateSafe(explicitDayKey);
    if (!dateObj && /^\d{4}-\d{2}-\d{2}$/.test(explicitDayKey)) {
      const [y, m, day] = explicitDayKey.split("-").map(Number);
      dateObj = new Date(y, m - 1, day);
    }
  } else {
    const rawDate = firstDefined(att, [
      "timestamp",
      "punchTime",
      "punchDateTime",
      "date",
      "Date",
      "attendanceDate",
      "createdAt",
      "inTime",
      "checkInTime",
      "checkIn",
      "timeStamp",
    ]);
    dateObj = toDateSafe(rawDate);
    if (dateObj) dayKey = ymd(dateObj);
  }
  if (!dateObj || !dayKey) return null;
  return { dayKey, dateObj };
}

/**
 * Map punch document to canonical employee id using the same lookup as monthly attendance.
 * @param {Map<string, string>} employeeLookup
 * @returns {string | null}
 */
export function resolveAttendanceDocEmployeeId(d, docSnapId, employeeLookup) {
  const att = d || {};
  const employeeId = String(
    firstDefined(att, [
      "employeeId",
      "empId",
      "uid",
      "user_id",
      "userId",
      "code",
      "employeeCode",
      "empCode",
      "EmpId",
    ]) ||
      docSnapId ||
      "unknown"
  );
  const fallbackName = String(firstDefined(att, ["name", "employeeName", "fullName", "empName"]) || employeeId);
  const matchKeyCandidates = [
    employeeId,
    docSnapId,
    firstDefined(att, ["email", "mail"]),
    firstDefined(att, ["username", "usernameLower", "user_name"]),
    firstDefined(att, ["name", "employeeName", "fullName", "empName", "Name", "EmployeeName"]),
    fallbackName,
  ].filter(Boolean);
  for (const k of matchKeyCandidates) {
    const raw = String(k).trim().toLowerCase();
    const n = norm(k);
    if (employeeLookup.has(raw)) return employeeLookup.get(raw);
    if (employeeLookup.has(n)) return employeeLookup.get(n);
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Employee-code matching (from July 2026)                                      */
/* -------------------------------------------------------------------------- */
/**
 * From 2026-07-01 onward, payroll resolves punches by matching the punch
 * machine code against `employeeCode` on the branch employee collection
 * (`Employees`, `Employees1`, …).
 *
 * Before that date, standard id-based matching (`Employees.employeeId`) still applies.
 */
export const HIKVISION_CUTOVER_DATE = new Date(2026, 6, 1); // 2026-07-01, local
export const EMPLOYEE_CODE_CUTOVER_DATE = HIKVISION_CUTOVER_DATE;

/** Employee-doc fields holding the canonical payroll id. */
const CANONICAL_ID_FIELDS = [
  "employeeId",
  "empId",
  "uid",
  "user_id",
  "userId",
  "code",
  "employeeCode",
  "EmployeeId",
];

/** Employee-doc fields that store the punching-machine / device code. */
const EMPLOYEE_CODE_SOURCE_FIELDS = ["employeeCode", "EmployeeCode"];

/** Punch-doc fields that may carry the device enrollment code. */
const PUNCH_CODE_FIELDS = [
  "employeeCode",
  "EmployeeCode",
  "employee_code",
  "employee_id",
  "empCode",
  "code",
  "employeeId",
  "empId",
  "EmployeeId",
];

/** True for the "Main Shop" attendance branch. */
export function isMainAttendanceShop(currentShop) {
  const label = String(currentShop?.label || "").trim().toLowerCase();
  return label === "main shop" || label === "main";
}

/** True once a punch date is at/after the employee-code cutover (July 2026+). */
export function isHikvisionCutoverActive(dateObj) {
  return (
    dateObj instanceof Date &&
    !Number.isNaN(dateObj.getTime()) &&
    dateObj.getTime() >= EMPLOYEE_CODE_CUTOVER_DATE.getTime()
  );
}

export function isEmployeeCodeCutoverActive(dateObj) {
  return isHikvisionCutoverActive(dateObj);
}

/**
 * Build `employeeCode -> canonical employeeId` for a branch employee collection.
 * @param {Iterable<import("firebase/firestore").QueryDocumentSnapshot>} employeeDocs
 * @returns {Map<string, string>}
 */
export function buildEmployeeCodeLookupForShop(_currentShop, employeeDocs) {
  const lookup = new Map();
  for (const docSnap of employeeDocs || []) {
    const d = docSnap.data() || {};
    if (!isWorkingEmployee(d)) continue;
    const canonical = String(firstDefined(d, CANONICAL_ID_FIELDS) || docSnap.id);
    const codes = EMPLOYEE_CODE_SOURCE_FIELDS.map((field) => d[field]).filter(
      (v) => v != null && String(v).trim() !== ""
    );
    for (const code of codes) {
      const raw = String(code).trim().toLowerCase();
      const n = norm(code);
      if (raw && !lookup.has(raw)) lookup.set(raw, canonical);
      if (n && !lookup.has(n)) lookup.set(n, canonical);
    }
  }
  return lookup;
}

/** @deprecated Use {@link buildEmployeeCodeLookupForShop}. */
export function buildMainNameLookupForShop(currentShop, employeeDocs) {
  return buildEmployeeCodeLookupForShop(currentShop, employeeDocs);
}

/** @deprecated Use {@link buildEmployeeCodeLookupForShop}. */
export async function buildHikvisionBridgeForShop(currentShop, employeeDocs) {
  return buildEmployeeCodeLookupForShop(currentShop, employeeDocs);
}

/** Match a punch doc to an employee id using device code fields only. */
function resolveByEmployeeCode(d, codeLookup) {
  if (!codeLookup?.size) return null;
  const roots = attendanceFieldRoots(d || {});
  const code = firstDefinedInRoots(roots, PUNCH_CODE_FIELDS);
  if (code == null || String(code).trim() === "") return null;
  const raw = String(code).trim().toLowerCase();
  if (codeLookup.has(raw)) return codeLookup.get(raw);
  const n = norm(code);
  if (codeLookup.has(n)) return codeLookup.get(n);
  return null;
}

/**
 * Resolve a punch doc to a canonical employee id, honoring the July 2026 code cutover.
 * Post-cutover, only punch code fields are used when a code lookup map is available.
 */
export function resolveAttendanceDocEmployeeIdCutover(d, docSnapId, dateObj, ctx = {}) {
  const { employeeLookup, employeeCodeLookup, mainNameLookup, hikBridge } = ctx;
  const codeLookup = employeeCodeLookup || mainNameLookup || hikBridge;
  if (codeLookup?.size > 0 && isEmployeeCodeCutoverActive(dateObj)) {
    return resolveByEmployeeCode(d, codeLookup);
  }
  return resolveAttendanceDocEmployeeId(d, docSnapId, employeeLookup);
}

/**
 * Raw-key resolver for surfaces that group by {@link extractEmpKeyFromData}
 * (OT / late / daily-hours). Post-cutover, punches map by code to the canonical
 * employee id; everything else keeps the existing raw-key behavior.
 */
export function extractEmpKeyWithCutover(d, docSnapId, dateObj, employeeCodeLookup) {
  if (employeeCodeLookup?.size > 0 && isEmployeeCodeCutoverActive(dateObj)) {
    return resolveByEmployeeCode(d, employeeCodeLookup);
  }
  return extractEmpKeyFromData(d, docSnapId);
}

/** Firestore fetch for punch docs in [rangeStart, rangeEndExclusive) — same strategies as monthly fetch. */
export async function fetchAttendanceForDateRange(collectionName, rangeStart, rangeEndExclusive) {
  const startStr = ymd(rangeStart);
  const endStr = ymd(rangeEndExclusive);
  const tryQuery = async (q) => {
    try {
      return await getDocs(q);
    } catch {
      return null;
    }
  };

  const qStr = query(
    collection(db, collectionName),
    where("timestamp", ">=", startStr),
    where("timestamp", "<", endStr)
  );
  const qTs = query(
    collection(db, collectionName),
    where("timestamp", ">=", Timestamp.fromDate(rangeStart)),
    where("timestamp", "<", Timestamp.fromDate(rangeEndExclusive))
  );

  const snapStr = await tryQuery(qStr);
  if (snapStr && snapStr.size > 0) {
    return { snap: snapStr, mode: "day (timestamp string)" };
  }
  const snapTs = await tryQuery(qTs);
  if (snapTs && snapTs.size > 0) {
    return { snap: snapTs, mode: "day (timestamp)" };
  }
  if (snapStr && snapTs && snapStr.size === 0 && snapTs.size === 0) {
    const probe = await tryQuery(query(collection(db, collectionName), limit(1)));
    if (probe && probe.size > 0) {
      const full = await getDocs(collection(db, collectionName));
      return { snap: full, mode: "full (day query empty; filter client)" };
    }
    return { snap: snapStr, mode: "day (empty)" };
  }
  const full = await getDocs(collection(db, collectionName));
  return { snap: full, mode: "full collection" };
}

/**
 * Build raw string → canonical employeeId map for punch matching (working employees only).
 * @param {{ employeeCandidates?: string[] }} currentShop
 */
export async function buildEmployeeLookupMap(currentShop) {
  const empCandidates = currentShop.employeeCandidates || [];
  const employeeCollectionUsed = await resolveCollectionName(empCandidates);
  if (!employeeCollectionUsed) {
    throw new Error("Employees collection not found");
  }
  const empSnap = await getDocs(collection(db, employeeCollectionUsed));
  const employeeLookup = new Map();
  for (const docSnap of empSnap.docs) {
    const d = docSnap.data() || {};
    if (!isWorkingEmployee(d)) continue;
    const employeeId = String(
      firstDefined(d, [
        "employeeId",
        "empId",
        "uid",
        "user_id",
        "userId",
        "code",
        "employeeCode",
        "EmployeeId",
      ]) || docSnap.id
    );
    const keyCandidates = [
      employeeId,
      firstDefined(d, [
        "employeeId",
        "empId",
        "uid",
        "user_id",
        "userId",
        "code",
        "employeeCode",
        "EmployeeId",
      ]),
      firstDefined(d, ["email", "mail"]),
      firstDefined(d, ["username", "usernameLower"]),
      firstDefined(d, [
        "name",
        "employeeName",
        "fullName",
        "empName",
        "Name",
        "EmployeeName",
      ]),
    ].filter(Boolean);
    for (const key of keyCandidates) {
      const raw = String(key).trim().toLowerCase();
      const n = norm(key);
      if (raw && !employeeLookup.has(raw)) employeeLookup.set(raw, employeeId);
      if (n && !employeeLookup.has(n)) employeeLookup.set(n, employeeId);
    }
  }
  const employeeCodeLookup = buildEmployeeCodeLookupForShop(currentShop, empSnap.docs);
  return {
    employeeLookup,
    employeeCollectionUsed,
    employeeCodeLookup,
    hikBridge: employeeCodeLookup,
    mainNameLookup: employeeCodeLookup,
  };
}

/**
 * Punch / attendance documents for one employee on one calendar day (local yyyy-mm-dd).
 * @returns {Promise<{ rows: object[], loadMode: string }>}
 */
export async function fetchPunchDocsForEmployeeOnDay(currentShop, canonicalEmployeeId, ymd) {
  const punchCandidates = currentShop.punchCandidates || [];
  const punchName = (await resolveCollectionName(punchCandidates)) || punchCandidates[0];
  if (!punchName) {
    throw new Error("Attendance collection not found");
  }
  const { employeeLookup, employeeCodeLookup, hikBridge } = await buildEmployeeLookupMap(currentShop);
  const codeLookup = employeeCodeLookup || hikBridge;
  const canon = String(canonicalEmployeeId || "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || "").trim());
  if (!m) {
    throw new Error("Invalid date");
  }
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const da = Number(m[3]);
  const dayStart = new Date(y, mo, da, 0, 0, 0, 0);
  const dayEndExclusive = new Date(y, mo, da + 1, 0, 0, 0, 0);
  const pack = await fetchAttendanceForDateRange(punchName, dayStart, dayEndExclusive);
  const rows = [];
  for (const docSnap of pack.snap.docs) {
    const d = docSnap.data() || {};
    const ctx = getAttendanceDocDayKeyAndDate(d, docSnap.id);
    if (!ctx) continue;
    const calKey = calendarDayKeyFromDate(ctx.dateObj);
    if (!calKey || calKey !== ymd) continue;
    const resolved = resolveAttendanceDocEmployeeIdCutover(d, docSnap.id, ctx.dateObj, {
      employeeLookup,
      employeeCodeLookup: codeLookup,
    });
    if (!resolved || resolved !== canon) continue;
    const times = collectPunchTimes(d);
    const timeStrs = times.map((t) =>
      t.toLocaleString("en-IN", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    );
    const w = inferAttendanceDocWeight(d);
    rows.push({
      id: docSnap.id,
      weight: w,
      times: timeStrs,
      branch: String(firstDefined(d, ["branch", "Branch", "shop", "outlet"]) || ""),
      type: String(firstDefined(d, ["type", "Type", "punchType", "PunchType"]) || ""),
      status: String(firstDefined(d, ["status", "Status", "attendanceType", "AttendanceType"]) || ""),
      halfFlag: w < 0.999,
    });
  }
  rows.sort((a, b) => (a.times[0] || "").localeCompare(b.times[0] || ""));
  return { rows, loadMode: pack.mode };
}

function safeHalfDayMarkPart(s) {
  return String(s || "")
    .trim()
    .replace(/[/\s#?[\]]/g, "_")
    .slice(0, 120);
}

/** Stable Firestore doc id for a manual half-day mark. */
export function payrollHalfDayMarkDocId(shopLabel, employeeId, ymd) {
  return `${safeHalfDayMarkPart(shopLabel)}___${safeHalfDayMarkPart(employeeId)}___${String(ymd || "").trim()}`;
}

/** Persist a manual ½-day for this employee + calendar day (merged into monthly attendance). */
export async function setPayrollHalfDayMark(currentShop, employeeId, ymd) {
  const shopLabel = String(currentShop?.label || "").trim();
  const eid = String(employeeId || "").trim();
  const y = String(ymd || "").trim();
  if (!shopLabel || !eid || !/^(\d{4})-(\d{2})-(\d{2})$/.test(y)) {
    throw new Error("Invalid shop, employee, or date");
  }
  const [Y, M] = y.split("-");
  const monthKey = `${Y}-${M}`;
  const id = payrollHalfDayMarkDocId(shopLabel, eid, y);
  const ref = doc(db, COLLECTION_PAYROLL_HALF_DAY_MARKS, id);
  await setDoc(
    ref,
    {
      shopLabel,
      employeeId: eid,
      ymd: y,
      monthKey,
      source: "payroll_web",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function deletePayrollHalfDayMark(currentShop, employeeId, ymd) {
  const shopLabel = String(currentShop?.label || "").trim();
  const eid = String(employeeId || "").trim();
  const y = String(ymd || "").trim();
  if (!shopLabel || !eid || !y) {
    throw new Error("Invalid shop, employee, or date");
  }
  const id = payrollHalfDayMarkDocId(shopLabel, eid, y);
  const ref = doc(db, COLLECTION_PAYROLL_HALF_DAY_MARKS, id);
  await deleteDoc(ref);
}

export async function payrollHalfDayMarkExists(currentShop, employeeId, ymd) {
  const shopLabel = String(currentShop?.label || "").trim();
  const eid = String(employeeId || "").trim();
  const y = String(ymd || "").trim();
  if (!shopLabel || !eid || !/^(\d{4})-(\d{2})-(\d{2})$/.test(y)) return false;
  const ref = doc(db, COLLECTION_PAYROLL_HALF_DAY_MARKS, payrollHalfDayMarkDocId(shopLabel, eid, y));
  const snap = await getDoc(ref);
  return !!snap.exists;
}

/**
 * @param {{ employeeCandidates?: string[], punchCandidates?: string[], label?: string }} currentShop
 * @param {Date} selectedMonth — first day of month
 * @returns {Promise<{ rows: object[], diagRows: object[], workingDays: string[], attendanceLoadMode: string }>}
 */
export async function loadMonthlyAttendanceRows(currentShop, selectedMonth) {
  const monthStart = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
  const monthEnd = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1);

  let workingDays = getWorkingDaysTillCutoff(selectedMonth, new Set());
  const diagRows = [];

  const employeeMap = new Map();
  const employeeLookup = new Map();
  let addedEmployees = 0;
  let skippedNonWorking = 0;

  const empCandidates = currentShop.employeeCandidates || [];
  const punchCandidates = currentShop.punchCandidates || [];

  const [employeeCollectionUsed, resolvedPunchCollection] = await Promise.all([
    resolveCollectionName(empCandidates),
    resolveCollectionName(punchCandidates),
  ]);
  if (!employeeCollectionUsed) {
    throw new Error("Employees collection not found");
  }
  const punchName = resolvedPunchCollection || punchCandidates[0];
  if (!punchName) {
    throw new Error("Attendance collection not found");
  }

  const cutoffDt = cutoffDateForSelectedMonth(selectedMonth);
  const [holidaySnap, empSnap, attPack] = await Promise.all([
    getDocs(collection(db, "holidays")).catch(() => ({ docs: [] })),
    getDocs(collection(db, employeeCollectionUsed)),
    fetchAttendanceForMonth(punchName, monthStart, monthEnd),
  ]);
  const holidayYmdSet = buildHolidayYmdSetFromDocs(holidaySnap.docs, monthStart, cutoffDt);
  workingDays = getWorkingDaysTillCutoff(selectedMonth, holidayYmdSet);
  const cashLeaveByEmp = buildEmployeeCashLeaveYmdByEmployee(holidaySnap.docs, monthStart, cutoffDt);
  const attendanceLoadMode = attPack.mode;
  let totalScanned = empSnap.size;
  for (const docSnap of empSnap.docs) {
    const d = docSnap.data() || {};
    if (!isWorkingEmployee(d)) {
      skippedNonWorking += 1;
      continue;
    }
    const employeeId = String(
      firstDefined(d, [
        "employeeId",
        "empId",
        "uid",
        "user_id",
        "userId",
        "code",
        "employeeCode",
        "EmployeeId",
      ]) || docSnap.id
    );
    const employeeName = String(
      firstDefined(d, [
        "name",
        "employeeName",
        "fullName",
        "empName",
        "username",
        "Name",
        "EmployeeName",
      ]) || employeeId
    );
    if (!employeeMap.has(employeeId)) {
      employeeMap.set(employeeId, {
        employeeId,
        employeeName,
        department: String(firstDefined(d, ["department", "dept", "Department"]) || ""),
        joiningDate: toDateSafe(
          firstDefined(d, [
            "DOJ",
            "doj",
            "joiningDate",
            "dateOfJoining",
            "DateOfJoining",
            "JoiningDate",
            "joinDate",
            "JoinDate",
            "date_joining",
            "dateOfJoin",
            "joining",
            "joinedAt",
            "joining_date",
            "createdAt",
            "created_at",
          ])
        ),
        /** @type {Record<string, number>} yyyy-mm-dd → cumulative day-units (capped at 1 per day when finalising) */
        unitsByDay: {},
        /** Working days with exactly one punch and no derived units (listed as present only, no ½-day credit). */
        thinPunchDays: new Set(),
      });
      addedEmployees += 1;
    }
    const keyCandidates = [
      employeeId,
      firstDefined(d, [
        "employeeId",
        "empId",
        "uid",
        "user_id",
        "userId",
        "code",
        "employeeCode",
        "EmployeeId",
      ]),
      firstDefined(d, ["email", "mail"]),
      firstDefined(d, ["username", "usernameLower"]),
      firstDefined(d, [
        "name",
        "employeeName",
        "fullName",
        "empName",
        "Name",
        "EmployeeName",
      ]),
    ].filter(Boolean);
    for (const key of keyCandidates) {
      const raw = String(key).trim().toLowerCase();
      const n = norm(key);
      if (raw && !employeeLookup.has(raw)) employeeLookup.set(raw, employeeId);
      if (n && !employeeLookup.has(n)) employeeLookup.set(n, employeeId);
    }
  }
  diagRows.push({
    collection: employeeCollectionUsed,
    type: "employees",
    scanned: totalScanned,
    used: addedEmployees,
    error: skippedNonWorking ? `Skipped non-working: ${skippedNonWorking}` : "",
  });

  const employeeCodeLookup = buildEmployeeCodeLookupForShop(currentShop, empSnap.docs);

  let scannedAttendance = 0;
  let usedAttendance = 0;
  const attSnap = attPack.snap;
  scannedAttendance = attSnap.size;
  /** @type {Map<string, { times: Date[], fieldSum: number }>} key = `${empId}|||${ymd}` */
  const punchByEmpDay = new Map();
  const getDayBucket = (key) => {
    if (!punchByEmpDay.has(key)) punchByEmpDay.set(key, { times: [], fieldSum: 0 });
    return punchByEmpDay.get(key);
  };

  for (const docSnap of attSnap.docs) {
    const d = docSnap.data() || {};
    const ctx = getAttendanceDocDayKeyAndDate(d, docSnap.id);
    if (!ctx) continue;
    const { dateObj, dayKey } = ctx;
    if (!dateObj || !dayKey || dateObj < monthStart || dateObj >= monthEnd) continue;

    const resolvedId = resolveAttendanceDocEmployeeIdCutover(d, docSnap.id, dateObj, {
      employeeLookup,
      employeeCodeLookup,
    });
    if (!resolvedId || !employeeMap.has(resolvedId)) continue;

    const fallbackName = String(
      firstDefined(d, ["name", "employeeName", "fullName", "empName"]) ||
        firstDefined(d, ["employeeId", "empId"]) ||
        resolvedId
    );
    const existing = employeeMap.get(resolvedId);
    if (
      fallbackName &&
      existing?.employeeName &&
      existing.employeeName.toLowerCase() === String(resolvedId).toLowerCase()
    ) {
      existing.employeeName = fallbackName;
    }

    const calKey = calendarDayKeyFromDate(dateObj) || dayKey;
    const bucketKey = `${resolvedId}|||${calKey}`;
    const bucket = getDayBucket(bucketKey);
    const times = collectPunchTimesAllRoots(d);
    for (const t of times) bucket.times.push(t);
    const wf = inferAttendanceDocWeightFromFields(d);
    const w = wf == null ? 0 : wf;
    bucket.fieldSum = Math.min(1, round2(bucket.fieldSum + w));
    usedAttendance += 1;
  }

  for (const [bucketKey, bucket] of punchByEmpDay) {
    const sep = bucketKey.indexOf("|||");
    if (sep < 0) continue;
    const empId = bucketKey.slice(0, sep);
    const dayKey = bucketKey.slice(sep + 3);
    if (!employeeMap.has(empId)) continue;
    const merged = mergeAndDedupeTimestamps(bucket.times, 10);
    const hours = workedHoursFromMergedTimesForDay(merged, dayKey);
    let u = 0;
    if (merged.length >= MULTI_PUNCH_FULL_DAY_MIN_COUNT) {
      u = 1;
    } else if (hours > 0) {
      u = dayUnitFromWorkedHours(hours);
    } else {
      u = Math.min(1, round2(bucket.fieldSum));
    }
    const empRow = employeeMap.get(empId);
    if (merged.length === 1 && u <= 0) {
      empRow.thinPunchDays.add(dayKey);
    } else if (u > 0) {
      empRow.unitsByDay[dayKey] = Math.min(1, round2(u));
    }
  }
  diagRows.push({
    collection: punchName,
    type: "attendance",
    scanned: scannedAttendance,
    used: usedAttendance,
    error: attendanceLoadMode || "",
  });

  const monthKey = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, "0")}`;
  const shopLabelForMarks = String(currentShop.label || "").trim();
  let marksScanned = 0;
  let marksApplied = 0;
  let marksError = "";
  try {
    const markSnap = await getDocs(
      query(collection(db, COLLECTION_PAYROLL_HALF_DAY_MARKS), where("monthKey", "==", monthKey))
    );
    marksScanned = markSnap.size;
    for (const docSnap of markSnap.docs) {
      const data = docSnap.data() || {};
      if (String(data.shopLabel || "").trim() !== shopLabelForMarks) continue;
      const eid = String(data.employeeId || "").trim();
      const ymdMark = String(data.ymd || "").trim();
      if (!eid || !/^(\d{4})-(\d{2})-(\d{2})$/.test(ymdMark)) continue;
      if (!employeeMap.has(eid)) continue;
      const empRow = employeeMap.get(eid);
      const prev = Number(empRow.unitsByDay[ymdMark]) || 0;
      empRow.unitsByDay[ymdMark] = Math.min(1, round2(prev + 0.5));
      marksApplied += 1;
    }
  } catch (e) {
    marksError = e?.message || String(e);
  }
  diagRows.push({
    collection: COLLECTION_PAYROLL_HALF_DAY_MARKS,
    type: "halfDayMarks",
    scanned: marksScanned,
    used: marksApplied,
    error: marksError,
  });

  const workingDaySet = new Set(workingDays);
  const finalRows = Array.from(employeeMap.values()).map((emp) => {
    const unitsByDay = { ...(emp.unitsByDay || {}) };
    const thinPunchDays = emp.thinPunchDays instanceof Set ? emp.thinPunchDays : new Set();
    let presentDayUnits = 0;
    const presentDates = [];
    const halfDayDates = [];
    for (const dayKey of workingDays) {
      const u = Math.min(1, round2(Number(unitsByDay[dayKey]) || 0));
      if (u >= 0.999) {
        presentDates.push(dayKey);
        presentDayUnits = round2(presentDayUnits + u);
      } else if (u > 0) {
        halfDayDates.push(dayKey);
        presentDayUnits = round2(presentDayUnits + u);
      } else if (thinPunchDays.has(dayKey)) {
        presentDates.push(dayKey);
      }
    }
    const punchedDays = new Set([...presentDates, ...halfDayDates]);
    const cashLeaveSet = employeeCashLeaveYmdSet(cashLeaveByEmp, emp.employeeId);
    /** Paid-leave working days in range (holidays collection `employee_cash_leave`). */
    const leaveDays = workingDays.filter((d) => cashLeaveSet.has(d)).length;
    const workingDaysForAbsence = workingDays.filter((d) => !cashLeaveSet.has(d));
    const absentDates = workingDaysForAbsence.filter((d) => !punchedDays.has(d));
    presentDates.sort();
    halfDayDates.sort();
    const presentUnitsByDay = {};
    for (const k of Object.keys(unitsByDay)) {
      const u = Math.min(1, round2(Number(unitsByDay[k]) || 0));
      if (u > 0) presentUnitsByDay[k] = u;
    }
    for (const d of thinPunchDays) {
      if (workingDaySet.has(d) && presentUnitsByDay[d] === undefined) {
        presentUnitsByDay[d] = 0;
      }
    }
    return {
      employeeId: emp.employeeId,
      employeeName: emp.employeeName,
      department: emp.department,
      tenureYears: yearsBetween(emp.joiningDate, cutoffDt),
      tenureLabel: (() => {
        const parts = tenurePartsBetween(emp.joiningDate, cutoffDt);
        if (!parts) return "—";
        return `${parts.years}y ${parts.months}m`;
      })(),
      month: monthTitle(selectedMonth),
      /** Sum of day-units on working days in range (includes ½ for half days). */
      presentDays: presentDayUnits,
      /** Count of working days marked paid leave (`employee_cash_leave`) for this employee. */
      leaveDays,
      presentDates,
      absentDates,
      halfDayDates,
      /** All calendar days in the month window with any punch weight (for weekly buckets, etc.). */
      presentUnitsByDay,
    };
  });
  finalRows.sort((a, b) => a.employeeName.localeCompare(b.employeeName));

  return {
    rows: finalRows,
    diagRows,
    workingDays,
    attendanceLoadMode,
    punchCollection: punchName,
    employeeCollection: employeeCollectionUsed,
  };
}
